import React, { createContext, useContext, useEffect, useState } from 'react';
import { logAnalyticsEvent } from './firebase';

type ThemeSource = 'system' | 'manual';

interface ThemeClasses {
  bg: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  cardBg: string;
  inputBg: string;
  buttonSecondary: string;
  featureCard: string;
  infoCard: string;
  headerBg: string;
  footerBg: string;
  errorBg: string;
  modeSelectorBg: string;
  accent: string;      // solid indigo accent for primary/active controls
  accentText: string;  // indigo text accent
}

interface ThemeContextValue {
  darkMode: boolean;
  themeSource: ThemeSource;
  toggleTheme: () => void;
  useSystemTheme: () => void;
  themeClasses: ThemeClasses;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function buildThemeClasses(darkMode: boolean): ThemeClasses {
  return {
    bg: darkMode ? 'bg-slate-950' : 'bg-slate-50',
    textPrimary: darkMode ? 'text-slate-100' : 'text-slate-900',
    textSecondary: darkMode ? 'text-slate-300' : 'text-slate-600',
    textMuted: darkMode ? 'text-slate-400' : 'text-slate-500',
    cardBg: darkMode
      ? 'bg-slate-900 border-slate-800'
      : 'bg-white border-slate-200 shadow-sm',
    inputBg: darkMode
      ? 'bg-slate-950 border-slate-700 text-slate-100 placeholder-slate-500 focus:border-indigo-500'
      : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400 focus:border-indigo-500',
    buttonSecondary: darkMode
      ? 'bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700 hover:text-white'
      : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-slate-900',
    featureCard: darkMode
      ? 'bg-slate-900 border-slate-800'
      : 'bg-white border-slate-200',
    infoCard: darkMode
      ? 'bg-slate-900 border-slate-800'
      : 'bg-white border-slate-200',
    headerBg: darkMode
      ? 'backdrop-blur bg-slate-950/80 border-slate-800'
      : 'backdrop-blur bg-white/80 border-slate-200',
    footerBg: darkMode
      ? 'bg-slate-900 border-slate-800'
      : 'bg-slate-50 border-slate-200',
    errorBg: darkMode
      ? 'bg-red-500/10 border-red-500/30 text-red-300'
      : 'bg-red-50 border-red-200 text-red-700',
    modeSelectorBg: darkMode
      ? 'bg-slate-800 border-slate-700'
      : 'bg-slate-100 border-slate-200',
    accent: 'bg-indigo-600 text-white hover:bg-indigo-500',
    accentText: darkMode ? 'text-indigo-400' : 'text-indigo-600',
  };
}

function prefersDark(): boolean {
  return typeof window !== 'undefined'
    && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

// react-snap prerenders with a "ReactSnap" user agent. We must not resolve the
// real (client-only) theme during prerender, otherwise the captured HTML would
// not match the client's initial render and hydration would fail.
function isPrerendering(): boolean {
  return typeof navigator !== 'undefined' && /ReactSnap/i.test(navigator.userAgent);
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [darkMode, setDarkMode] = useState(true);
  const [themeSource, setThemeSource] = useState<ThemeSource>('system');

  // Initialize theme from localStorage or default to system preference.
  // Skipped during prerender so the prerendered markup keeps the deterministic
  // default (dark) that the client also renders first — avoiding a hydration
  // mismatch. The client then applies the real theme right after hydration.
  useEffect(() => {
    if (isPrerendering()) return;
    const savedTheme = localStorage.getItem('base64-theme');
    const savedThemeSource = localStorage.getItem('base64-theme-source');

    if (savedThemeSource === 'manual' && savedTheme) {
      setDarkMode(savedTheme === 'dark');
      setThemeSource('manual');
    } else {
      setThemeSource('system');
      setDarkMode(prefersDark());
    }
  }, []);

  // Follow system theme changes while in system mode.
  useEffect(() => {
    if (themeSource !== 'system') return;
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      setDarkMode(e.matches);
      logAnalyticsEvent('system_theme_change', { theme: e.matches ? 'dark' : 'light' });
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [themeSource]);

  // Persist preference.
  useEffect(() => {
    localStorage.setItem('base64-theme', darkMode ? 'dark' : 'light');
    localStorage.setItem('base64-theme-source', themeSource);
  }, [darkMode, themeSource]);

  const toggleTheme = () => {
    setThemeSource('manual');
    setDarkMode((prev) => !prev);
    logAnalyticsEvent('toggle_theme', { theme: !darkMode ? 'dark' : 'light', source: themeSource });
  };

  const useSystemTheme = () => {
    setThemeSource('system');
    const systemPrefersDark = prefersDark();
    setDarkMode(systemPrefersDark);
    logAnalyticsEvent('use_system_theme', { system_theme: systemPrefersDark ? 'dark' : 'light' });
  };

  const value: ThemeContextValue = {
    darkMode,
    themeSource,
    toggleTheme,
    useSystemTheme,
    themeClasses: buildThemeClasses(darkMode),
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return ctx;
}
